/** Base path for all service request API endpoints. */
const SERVICE_REQUEST_BASE_PATH =
  'loopay-service-request-management/service-requests';

/**
 * API endpoint paths for the service request management domain.
 *
 * @example
 * serviceRequestEndpoints.SERVICE_REQUESTS
 * // 'loopay-service-request-management/service-requests'
 *
 * serviceRequestEndpoints.VIEW_SERVICE_REQUEST('abc-123')
 * // 'loopay-service-request-management/service-requests/abc-123'
 */
const serviceRequestEndpoints = {
  SERVICE_REQUESTS: SERVICE_REQUEST_BASE_PATH,
  MY_ASSIGNED_TASKS: `${SERVICE_REQUEST_BASE_PATH}/my-tasks`,
  VIEW_SERVICE_REQUEST: (id: string) => `${SERVICE_REQUEST_BASE_PATH}/${id}`,
  /**
   * Self-scoped detail of a single task assigned to the calling admin (RR-914).
   * Returns the task only when it is assigned to the caller (ADMIN /
   * SUPER_ADMIN); a request that does not exist, belongs to someone else, or is
   * assigned to a technician → 404. Same response shape as
   * {@link VIEW_SERVICE_REQUEST} minus the technician-only `canAcknowledge`.
   */
  VIEW_MY_ASSIGNED_TASK: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/my-tasks/${id}`,
  UPDATE_SERVICE_REQUEST_STATUS: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/status`,
  ELIGIBLE_TECHNICIANS: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/eligible-technicians`,
  /**
   * Canonical eligible-assignee listing (RR-912). Filtered by a required
   * `?type=technician|admin` query param. The legacy `ELIGIBLE_TECHNICIANS`
   * route is retained as a deprecated alias (implies `type=technician`) so the
   * technician flow keeps working unchanged.
   */
  ELIGIBLE_ASSIGNEES: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/eligible-assignees`,
  ASSIGN_TECHNICIAN: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/assign`,
  RESCHEDULE_SERVICE_REQUEST: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/reschedule`,
  /**
   * Accept/reject an assigned task. Shared by the assigned TECHNICIAN and, since
   * RR-1106, the ADMIN / SUPER_ADMIN the task is assigned to — the backend
   * resolves the caller from the auth token, enforces ownership (403 when the SR
   * is not assigned to the caller) and the `ASSIGNED` status window (409
   * otherwise). Body is `{ status, reason? }`.
   */
  ACKNOWLEDGE_SERVICE_REQUEST: (id: string) =>
    `${SERVICE_REQUEST_BASE_PATH}/${id}/acknowledge`,
  /**
   * Shared media-management batch presigned-URL endpoint. Used to provisionally
   * upload the files an admin assignee attaches when completing a service
   * request, before the `attachments` array is sent on the status PATCH.
   * Returns one signed PUT URL (+ `path` / `resourceId`) per requested path;
   * provisional uploads are auto-expired by the S3 lifecycle rule unless the
   * backend re-tags them as confirmed on commit.
   */
  PRESIGNED_URLS_BATCH: 'loopay-media-management/media/presigned-urls/batch',
  /**
   * Shared media-management resource endpoint. DELETE removes a provisional S3
   * object the user attached then removed before submitting.
   */
  MEDIA_RESOURCES: 'loopay-media-management/media/resources',
} as const;

export default serviceRequestEndpoints;
